import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChallengeActivityQuery, ChallengeActivityService, ChallengeActivityStore, ChallengeActivityStoreModel } from '@examdojo/challenge';
import { McqAttemptResponseService, QuestionAttemptEntityQuery, QuestionAttemptEntityService } from '@examdojo/question';
import { catchError, combineLatest, EMPTY, of, switchMap } from 'rxjs';
import { ExamdojoPlatformService } from '../../../shared/platform.service';
import { ChallengeDialogService } from '../dialog/challenge-dialog.service';

@Injectable()
export class SyncChallengeActivityStateService {
  constructor(
    private readonly challengeActivityService: ChallengeActivityService,
    private readonly challengeActivityQuery: ChallengeActivityQuery,
    private readonly questionAttemptEntityService: QuestionAttemptEntityService,
    private readonly questionAttemptQuery: QuestionAttemptEntityQuery,
    private readonly platformService: ExamdojoPlatformService,
    private readonly mcqResponseService: McqAttemptResponseService,
    private readonly challengeDialogService: ChallengeDialogService,
    private readonly challengeActivityStore: ChallengeActivityStore,
  ) {
    this.syncStoreWhenBackOnline().pipe(takeUntilDestroyed()).subscribe();
  }

  private syncStoreWhenBackOnline() {
    return this.platformService.trigger$.pipe(
      switchMap(() =>
        this.syncChallengeActivityState().pipe(
          catchError(() => {
            this.challengeActivityStore.setActive(null);
            return EMPTY;
          }),
        ),
      ),
    );
  }

  private syncChallengeActivityState() {
    const activeId = this.challengeActivityQuery.getActiveId();
    const isDialogOpen = this.challengeDialogService.isOpen();

    return this.challengeActivityService.fetchOngoing(isDialogOpen && activeId ? activeId : undefined).pipe(
      switchMap(() => {
        if (!activeId || !isDialogOpen) {
          return of(null);
        }
        return this.syncActiveActivity(activeId);
      }),
    );
  }

  private syncActiveActivity(activeId: ChallengeActivityStoreModel['id']) {
    const activity = this.challengeActivityQuery.getEntity(activeId);
    if (!activity) {
      return of(null);
    }

    if (activity.status === 'SUBMITTED') {
      this.questionAttemptEntityService.markAllPendingAttemptsAsSubmittedLocally();
      return of(null);
    }

    return combineLatest([
      this.challengeActivityService.fetchMissingQuestionAttempts(activeId),
      this.challengeActivityService.syncPostGradedChallengeActivity(),
      this.syncMcqResponses(),
    ]);
  }

  private syncMcqResponses() {
    const mcqAttemptIds = this.questionAttemptQuery
      .getAllEntities()
      .filter((entity) => this.questionAttemptQuery.isQuestionMultipleChoice(entity.id))
      .map((entity) => entity.id);

    if (mcqAttemptIds.length === 0) {
      return of(null);
    }

    return this.mcqResponseService.fetchByQuestionAttemptIds(mcqAttemptIds);
  }
}
